import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { getQuoteChangeOrder } from '../../../gql/ChangeOrder/queries/GetQuoteChangeOrder';
import { QuoteChangeOrder } from '../../../interfaces/ChangeOrderBackendModel';
import { AppDispatch, RootState } from '../../../store';
import { UIInput } from '../../../types/uiTypes';
import { convertSystemErrorToString } from '../../../util/errorUtils';

export const SLICE_NAME = 'changeOrderDetailView';

export interface ChangeOrderDetailViewState {
  changeOrderEvent: QuoteChangeOrder | null;
  status: {
    isInitiatingDetailView: boolean;
  };
  errors: {
    initiatingDetailViewError: string;
  };
}

interface SetStatusParam {
  statusKey: keyof ChangeOrderDetailViewState['status'];
  status: ChangeOrderDetailViewState['status'][keyof ChangeOrderDetailViewState['status']];
}

interface SetErrorParam {
  errorKey: keyof ChangeOrderDetailViewState['errors'];
  error: ChangeOrderDetailViewState['errors'][keyof ChangeOrderDetailViewState['errors']];
}

export interface UpdateChangeOrderLevelInfoParam {
  field: keyof QuoteChangeOrder;
  value: any;
}

export interface UpdateChangeOrderPropertyParam {
  name: string;
  value: string;
}

const initialState: ChangeOrderDetailViewState = {
  changeOrderEvent: null,
  status: {
    isInitiatingDetailView: false
  },
  errors: {
    initiatingDetailViewError: ''
  }
};

const changeOrderDetailViewSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setChangeOrderEvent(state, action: PayloadAction<QuoteChangeOrder>) {
      state.changeOrderEvent = action.payload;
    },
    setChangeOrderViewStatus(state, action: PayloadAction<SetStatusParam>) {
      const { statusKey, status } = action.payload;
      state.status[statusKey] = status;
    },
    setChangeOrderViewError(state, action: PayloadAction<SetErrorParam>) {
      const { errorKey, error } = action.payload;
      state.errors[errorKey] = error;
    },
    updateChangeOrderLevelInfo(state, action: PayloadAction<UpdateChangeOrderLevelInfoParam>) {
      const { field, value } = action.payload;
      if (state.changeOrderEvent) {
        state.changeOrderEvent = {
          ...state.changeOrderEvent,
          [field]: value
        };
      }
    },
    updateChangeOrderProperty(state, action: PayloadAction<UpdateChangeOrderPropertyParam>) {
      const { name, value } = action.payload;
      if (state.changeOrderEvent) {
        const propertyIndex = state.changeOrderEvent.properties.findIndex(p => p.name === name);
        if (propertyIndex !== -1) {
          state.changeOrderEvent.properties[propertyIndex] = { name, value };
        } else {
          state.changeOrderEvent.properties.push({ name, value });
        }
      }
    },
  }
});

export interface InitializeDetailViewUIInput extends UIInput {
  changeOrderId: string;
}

 /**
  * Initialize Change Order detail view state by fetching the change order data
  */
export const onInitializeChangeOrderView = createAsyncThunk<
  void,
  InitializeDetailViewUIInput,
  {
    dispatch: AppDispatch;
    state: RootState;
  }
>(
  `${SLICE_NAME}/onInitializeChangeOrderView`,
  async ({ apolloClient, changeOrderId }, { dispatch }): Promise<void> => {
    try {
      dispatch(setChangeOrderViewStatus({ statusKey: 'isInitiatingDetailView', status: true }));
      const changeOrder = await getQuoteChangeOrder(apolloClient, changeOrderId);
      dispatch(setChangeOrderEvent(changeOrder));
    } catch (error) {
      const sanitizedError = convertSystemErrorToString(error);
      dispatch(setChangeOrderViewError({ errorKey: 'initiatingDetailViewError', error: sanitizedError }));
    } finally {
      dispatch(setChangeOrderViewStatus({ statusKey: 'isInitiatingDetailView', status: false }));
    }
  }
);

export const {
  setChangeOrderEvent,
  setChangeOrderViewStatus,
  setChangeOrderViewError,
  updateChangeOrderLevelInfo,
  updateChangeOrderProperty
} = changeOrderDetailViewSlice.actions;

export const selectChangeOrderViewState = (state: RootState) => ({
  changeOrderEvent: state[SLICE_NAME].changeOrderEvent,
  isLoading: state[SLICE_NAME].status.isInitiatingDetailView,
  error: state[SLICE_NAME].errors.initiatingDetailViewError
});

export default changeOrderDetailViewSlice.reducer;
